import { faGlobe } from '@fortawesome/pro-regular-svg-icons';
import classnames from 'classnames';
import { useState } from 'react';
import { twMerge } from 'tailwind-merge';

import { LOCALE_ES, useI18nReact, useTranslate } from '@growtherapy/i18n';
import { Button, Icon } from '@growtherapy/sprout-ui';

import { useIsScreenBelowBreakpoint } from '../hooks/useIsScreenBelowBreakpoint';
import { useTheme } from '../hooks/useTheme';
import { Theme } from '../state/theme';
import { LanguageSwitcherModal, LanguageSwitcherModalCallbacks } from './LanguageSwitcherModal';

/** Inline style overrides secondary + rebrand:dark utilities. */
const DESKTOP_LANGUAGE_SWITCHER_DARK_SCRIM = 'rgba(0, 0, 0, 0.3)';

export type LanguageSwitcherProps = LanguageSwitcherModalCallbacks & {
  onOpenSwitcher?: (currentLanguage: string) => void;
};

export function LanguageSwitcher({
  onOpenSwitcher,
  onModalViewed,
  onLanguageSaved,
  onLanguageChangeError,
}: LanguageSwitcherProps = {}) {
  const { theme } = useTheme();
  const isBelowSm = useIsScreenBelowBreakpoint('sm');
  const t = useTranslate();
  const { language } = useI18nReact();
  const [isModalOpen, setIsModalOpen] = useState(false);

  const languageCode = language.toUpperCase();
  const changeLanguageAriaLabel = (() => {
    switch (language) {
      case LOCALE_ES:
        return t('telehealth-core:settings.changeLanguageSpanish');
      default:
        return t('telehealth-core:settings.changeLanguageEnglish');
    }
  })();

  const handleOpenSwitcher = (): void => {
    onOpenSwitcher?.(language);
    setIsModalOpen(true);
  };

  return (
    <>
      {isBelowSm ? (
        <Button
          use='unstyled'
          aria-label={changeLanguageAriaLabel}
          className={twMerge(
            classnames({
              'text-lilac-700 rebrand:text-action-primary-textLink': theme === Theme.LIGHT,
              'text-neutral-100 rebrand:!text-text-inverse': theme === Theme.DARK,
            }),
            'flex items-center justify-center px-0 py-0 rebrand:px-0 rebrand:py-0 hover:brightness-85 active:brightness-70',
          )}
          onClick={handleOpenSwitcher}
        >
          <Icon icon={faGlobe} aria-hidden />
        </Button>
      ) : (
        <Button
          use='secondary'
          aria-label={changeLanguageAriaLabel}
          style={theme === Theme.DARK ? { backgroundColor: DESKTOP_LANGUAGE_SWITCHER_DARK_SCRIM } : undefined}
          size='sm'
          className={twMerge(
            classnames({
              'text-lilac-700 rebrand:text-action-primary-textLink': theme === Theme.LIGHT,
              'text-neutral-100 rebrand:!text-text-inverse': theme === Theme.DARK,
            }),
            'flex items-center gap-125 rounded-full',
          )}
          onClick={handleOpenSwitcher}
        >
          <Icon icon={faGlobe} aria-hidden />
          <span aria-hidden>{languageCode}</span>
        </Button>
      )}
      <LanguageSwitcherModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onModalViewed={onModalViewed}
        onLanguageSaved={onLanguageSaved}
        onLanguageChangeError={onLanguageChangeError}
      />
    </>
  );
}
